import { useState } from 'react'
import { Head, Form, Link } from '@inertiajs/react'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Field } from '@/components/ui/Field'

type Labels = {
  title: string
  description: string
  keg_prefix: string
  keg_prefix_help: string
  preview_intro: { one: string; other: string }
  quantity: string
  current_beer: string
  submit: string
  submit_processing: string
  back_link: string
}

type Props = {
  keg_prefix: string | null
  suggested_keg_prefix: string | null
  labels: Labels
}

// What the identifiers will read once created. A brand-new prefix is unique platform-wide, so its
// numbering necessarily starts at 1 — no server round-trip needed to preview it.
function previewIdentifiers(prefix: string, quantity: number) {
  if (!prefix || quantity < 1) return null
  if (quantity <= 3) {
    return Array.from({ length: quantity }, (_, i) => `${prefix}-${i + 1}`).join(', ')
  }
  return `${prefix}-1, ${prefix}-2 … ${prefix}-${quantity}`
}

export default function KegsNew({ keg_prefix, suggested_keg_prefix, labels }: Props) {
  const naming = !keg_prefix
  const [prefix, setPrefix] = useState(suggested_keg_prefix ?? '')
  const [quantity, setQuantity] = useState(1)
  const preview = naming ? previewIdentifiers(prefix, quantity) : null

  return (
    <>
      <Head title={labels.title} />
      <div className="mx-auto max-w-xl px-4 py-10 sm:px-6 lg:px-8">
        <Link href="/kegs" className="text-sm font-semibold text-ink-soft hover:text-ink">
          {labels.back_link}
        </Link>
        <h1 className="mt-4 font-display text-3xl text-ink">{labels.title}</h1>
        <p className="mt-2 text-sm text-ink-soft">{labels.description}</p>

        <Card className="mt-6 p-6 sm:p-8">
          <Form method="post" action="/kegs/batch" className="space-y-5">
            {({ errors, processing }) => (
              <>
                {naming && (
                  <Field
                    label={labels.keg_prefix}
                    name="keg_prefix"
                    type="text"
                    hint={labels.keg_prefix_help}
                    value={prefix}
                    onChange={(event) =>
                      setPrefix(
                        event.target.value
                          .toUpperCase()
                          .replace(/[^A-Z0-9]/g, '')
                          .slice(0, 8),
                      )
                    }
                    maxLength={8}
                    required
                    autoFocus
                    error={errors.keg_prefix}
                  />
                )}
                {/* Tinted, unlike the plain-text hints that sit above every input: this is the
                    consequence of the choice being made, not an instruction about the next field. */}
                {preview && (
                  <p className="rounded-xl bg-paper px-3.5 py-2.5 text-sm text-ink-soft">
                    {quantity === 1 ? labels.preview_intro.one : labels.preview_intro.other}{' '}
                    <span className="font-display text-ink">{preview}</span>
                  </p>
                )}
                <Field
                  label={labels.quantity}
                  name="quantity"
                  type="number"
                  min={1}
                  max={1000}
                  value={quantity}
                  onChange={(event) => setQuantity(Number(event.target.value))}
                  required
                  autoFocus={!naming}
                  error={errors.quantity}
                />
                <Field label={labels.current_beer} name="current_beer" type="text" error={errors.current_beer} />

                <Button type="submit" disabled={processing} className="w-full disabled:opacity-60">
                  {processing ? labels.submit_processing : labels.submit}
                </Button>
              </>
            )}
          </Form>
        </Card>
      </div>
    </>
  )
}
